import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Sincroniza el estado de un `<FilterPanel>` con los query params de la URL
 * — gap FieldData (vista de datos filtrada por lote), ver
 * docs/superpowers/specs/2026-09-12-filterpanel-vista-lote-design.md.
 *
 * Cada clave del esquema se serializa como un solo query param: arrays como
 * CSV (`?type=RIEGO,FERTILIZACION`), strings sueltos tal cual
 * (`?from=2026-09-01`). Ausente en la URL = filtro no aplicado.
 */
export function useUrlFilters<K extends string>(keys: readonly K[]) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    const result = {} as Record<K, string[]>;
    for (const key of keys) {
      const raw = searchParams.get(key);
      result[key] = raw ? raw.split(',').filter(Boolean) : [];
    }
    return result;
  }, [searchParams, keys]);

  const setFilter = useCallback((key: K, values: string[]) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (values.length === 0) next.delete(key);
      else next.set(key, values.join(','));
      return next;
    });
  }, [setSearchParams]);

  const clearFilter = useCallback((key: K) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete(key);
      return next;
    });
  }, [setSearchParams]);

  const clearAll = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const key of keys) next.delete(key);
      return next;
    });
  }, [setSearchParams, keys]);

  const hasActiveFilters = useMemo(
    () => keys.some(key => filters[key].length > 0),
    [keys, filters],
  );

  return { filters, setFilter, clearFilter, clearAll, hasActiveFilters };
}
